/**
 * The connection dialog's live layout (Option C, C1): the geometry for the current viewport (`dialogGeometry.ts`) plus
 * the measured very-short tier. Parts that stay fixed in the short tier carry `data-dialog-fixed`; their heights (and the
 * dialog's 2 px border) decide whether the form region would drop under 96 px.
 *
 * `beforeViewportChange` runs on every resize BEFORE the new layout renders, so the focus mapping can record which
 * control had focus while it is still in the document (`useFocusRoleMapping`).
 */
import { useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { DIALOG_BORDER, dialogGeometry, isVeryShort, type DialogGeometry, type DialogMode, type Viewport } from './dialogGeometry';

export type DialogLayout = DialogGeometry & { veryShort: boolean };

const readViewport = (): Viewport => ({ width: window.innerWidth, height: window.innerHeight });

function fixedPartsHeight(root: HTMLElement): number {
  let total = DIALOG_BORDER;
  root.querySelectorAll<HTMLElement>('[data-dialog-fixed]').forEach((part) => {
    total += part.offsetHeight;
  });
  return total;
}

type Options = {
  mode: DialogMode;
  /**
   * The dialog content element (measured for the very-short tier). State, not a ref: Radix mounts the portal content a
   * render after the dialog, and the measurement must run once it exists.
   */
  root: HTMLElement | null;
  beforeViewportChange?: MutableRefObject<() => void>;
  /** Changes when the set of fixed parts may change (view, results shown): re-measure. */
  measureKey: string;
};

export function useDialogLayout({ mode, root, beforeViewportChange, measureKey }: Options): DialogLayout {
  const [viewport, setViewport] = useState(readViewport);
  const [fixedHeight, setFixedHeight] = useState(0);
  const geometry = dialogGeometry(viewport, mode);
  const veryShort = geometry.short && isVeryShort(viewport.height, fixedHeight);
  const observed = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const onResize = () => {
      beforeViewportChange?.current();
      setViewport(readViewport());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [beforeViewportChange]);

  // Only the short tier needs the measurement; it re-measures when a fixed part resizes (the footer wrapping to two rows)
  // and when the parts move (the very-short tier re-mounts them inside the scroll region).
  useLayoutEffect(() => {
    observed.current?.disconnect();
    observed.current = null;
    if (!geometry.short || !root) return;
    const measure = () => setFixedHeight(fixedPartsHeight(root));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    root.querySelectorAll('[data-dialog-fixed]').forEach((part) => observer.observe(part));
    observed.current = observer;
    return () => observer.disconnect();
  }, [geometry.short, geometry.columns, measureKey, root, viewport.width, veryShort]);

  return { ...geometry, veryShort };
}
